import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /* LMM (2026-07-29): Enter im Mengenfeld der Warenkorbseite.

     Gemessener Ausgangsstand (live, 2026-07-29): Das Mengenfeld (name="updates[]") liegt in
     <form id="cart-form" action="/cart" method="post">. Einziger Submit-Button dieses
     Formulars ist der Kasse-Button #checkout (name="checkout", per form="cart-form"
     angehängt). Enter löste deshalb die implizite Formularabsendung mit genau diesem
     Default-Button aus — gemessen: submit auf #cart-form mit submitter #checkout,
     defaultPrevented=false, und quantity-selector:update wurde dabei NICHT gesendet. Der
     Kunde landete also unerwartet in der Kasse, während die Menge nur als Formularwert
     mitlief. Horizon hat für Enter keinen eigenen Handler: component-quantity-selector.js
     kennt ausschließlich on:blur="/setQuantity" und on:focus="/selectInputValue".

     Enter nimmt jetzt exakt denselben Weg wie Blur — setQuantity() prüft die Eingabe, rundet
     auf gültige Schritte und meldet die Menge über QuantitySelectorUpdateEvent an
     cart-items-component. Kein zweiter Warenkorbweg, kein eigener Request.

     Der Handler hängt bewusst NUR an dieser Warenkorb-Unterklasse und nur am Mengenfeld:
     Gutscheinfeld, Bestellnummer-/Bemerkungsfeld, Kasse-Button, Express-Buttons, Links und
     das Mengenfeld der Produktseite bleiben unberührt. Der Listener sitzt auf der Komponente
     selbst, wird also weder doppelt registriert noch überlebt er sie. */

  /** @type {number | null} Zuletzt per Enter übernommene Menge (Doppelsende-Sperre für Blur). */
  #enterWert = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#beiTaste);
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.#beiTaste);
    super.disconnectedCallback();
  }

  /**
   * Übernimmt die Menge bei Enter, statt das Warenkorbformular abzuschicken.
   * @param {KeyboardEvent} event
   */
  #beiTaste = (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;

    const feld = this.refs.quantityInput;
    if (!feld || event.target !== feld) return;

    // Verhindert den impliziten Submit von #cart-form und damit den Sprung in die Kasse.
    event.preventDefault();

    const roh = feld.value.trim();
    const wunsch = /^\d+$/.test(roh) ? parseInt(roh, 10) : NaN;

    if (!Number.isFinite(wunsch) || wunsch < 1) {
      // Leeres Feld, 0 oder unbrauchbare Eingabe: zuletzt vom Server bestätigte Menge
      // wiederherstellen (das value-Attribut kommt aus dem Liquid und wird bei jedem
      // Section-Rendering neu gesetzt). Nichts senden, nichts navigieren.
      feld.value = feld.defaultValue;
      this.updateButtonStates();
      this.#enterWert = null;
      return;
    }

    this.setQuantity(event);
    this.#enterWert = parseInt(feld.value, 10);
  };

  /**
   * Wie in der Basisklasse — nur dass ein Blur unmittelbar nach einer Enter-Übernahme
   * dieselbe Menge nicht ein zweites Mal an Shopify schickt. Jede andere Eingabe und jede
   * Änderung des Werts hebt die Sperre auf.
   * @param {Event} event
   */
  setQuantity(event) {
    if (
      event?.type === 'blur' &&
      this.#enterWert !== null &&
      parseInt(this.refs.quantityInput.value, 10) === this.#enterWert
    ) {
      this.#enterWert = null;
      return;
    }

    this.#enterWert = null;
    super.setQuantity(event);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
